import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, type Observable } from 'rxjs';
import { LOADER_CONTEXT } from '../../../../services/interceptors/loader.interceptor';
import { UserSettingsStartPage, UserSettingsStartPageUtil } from '../user-settings-start-page.util';
import {
  languageDefaultValue,
  startPageDefaultValueOnAPISide,
  UserSettingsLocale,
  type UserSettingsApiResponse,
  type UserSettings,
  type UserSettingsRequest
} from './user-settings-api.type';

@Injectable({
  providedIn: 'root'
})
export class UserSettingsAPIService {
  private readonly httpClient = inject(HttpClient);

  read(): Observable<UserSettings> {
    return this.httpClient
      .get<UserSettingsApiResponse>(`/api/user/user-settings`, {
        context: new HttpContext().set(LOADER_CONTEXT, true)
      })
      .pipe(map(response => this.parseResponse(response)));
  }

  update(settings: UserSettingsRequest): Observable<UserSettings> {
    return this.httpClient
      .post<UserSettingsApiResponse>(`/api/user/user-settings`, settings, {
        context: new HttpContext().set(LOADER_CONTEXT, true)
      })
      .pipe(map(response => this.parseResponse(response)));
  }

  private parseResponse(userSettings: UserSettingsApiResponse): UserSettings {
    return {
      ...userSettings,
      notificationLanguage: this.convertLanguage(userSettings.notificationLanguage),
      startPage: this.convertStartPage(userSettings.startPage),
      systemLanguage: this.convertLanguage(userSettings.systemLanguage)
    };
  }

  private convertLanguage(language: string): UserSettingsLocale {
    const locales: ReadonlySet<UserSettingsLocale> = new Set(Object.values(UserSettingsLocale));
    return locales.has(language as UserSettingsLocale) ? (language as UserSettingsLocale) : languageDefaultValue;
  }

  private convertStartPage(startPage: string): UserSettingsStartPage {
    if (UserSettingsStartPageUtil.hasCodename(startPage as UserSettingsStartPage)) {
      return startPage as UserSettingsStartPage;
    }
    if (startPage !== startPageDefaultValueOnAPISide) {
      throw new Error(`The starting page codename ('${startPage}') does not exist.`);
    }
    return UserSettingsStartPageUtil.startPageByDefault;
  }
}
